import { ThunkAction } from 'redux-thunk';
import * as Bixby from '$/core';
import { AppState } from './..';
import { UserAdminService } from '$/services';

import {
  USERS_LOADING,
  USERS_SUCCESS,
  USERS_FAILURE,
  USER_LOADING,
  USER_SUCCESS,
  USER_FAILURE,
  USER_DELETE,
  USER_DELETE_SUCCESS,
  USER_DELETE_FAILURE,
  CREATE_USER,
  CREATE_USER_SUCCESS,
  CREATE_USER_FAILURE,
  USER_UPDATE_SUCCESS,
  USER_UPDATE_FAILURE,
} from './types';
import { UserSlim, UpdateUserRequest } from '$/core/models';
import { toastError, toastSuccess } from '$/store/toast/actions';

// action creators
export const loadUsersAction = () => {
  return {
    type: USERS_LOADING,
  };
};

export const usersSuccessAction = (users: Bixby.Models.UserSlim[]) => {
  return {
    type: USERS_SUCCESS,
    payload: users,
  };
};

export const usersFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: USERS_FAILURE,
    payload: error,
    error: true,
  };
};

export const loadUserAction = () => {
  return {
    type: USER_LOADING,
  };
};

export const clientSuccessAction = (user?: Bixby.Models.User) => {
  return {
    type: USER_SUCCESS,
    payload: user,
  };
};

export const clientFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: USER_FAILURE,
    payload: error,
    error: true,
  };
};

export const createUserAction = (request: Bixby.Models.CreateUserRequest) => {
  return {
    type: CREATE_USER,
    payload: request,
  };
};

export const createUserSuccessAction = (user: Bixby.Models.User) => {
  return {
    type: CREATE_USER_SUCCESS,
    payload: user,
  };
};

export const createUserFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: CREATE_USER_FAILURE,
    payload: error,
  };
};

export const deleteUserAction = () => {
  return {
    type: USER_DELETE,
  };
};

export const deleteUserSuccessAction = () => {
  return {
    type: USER_DELETE_SUCCESS,
  };
};

export const deleteUserFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: USER_DELETE_FAILURE,
    payload: error,
    error: true,
  };
};

export const userUpdateSuccessAction = (user: Bixby.Models.User) => {
  return {
    type: USER_UPDATE_SUCCESS,
    payload: user,
  };
};
export const userUpdateFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: USER_UPDATE_FAILURE,
    payload: error,
    error: true,
  };
};

// thunk actions
export const getUsers = (): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> => async dispatch => {
  dispatch(loadUsersAction());
  try {
    const allUsers = await UserAdminService.getAll();
    dispatch(usersSuccessAction(allUsers));
  } catch (error: any) {
    dispatch(usersFailureAction(error));
  }
};

export const deleteUser =
  (user: Bixby.Models.UserSlim): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(deleteUserAction());
    try {
      await UserAdminService.deleteById(user);
      await dispatch(getUsers());
      dispatch(deleteUserSuccessAction());
      await dispatch(toastSuccess(`${user.firstName} ${user.lastName} deleted`));
    } catch (error: any) {
      await dispatch(toastError(`Failed to delete ${user.firstName} ${user.lastName}`));
      dispatch(deleteUserFailureAction(error));
    }
  };

export const createUser =
  (request: Bixby.Models.CreateUserRequest): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    await dispatch(createUserAction(request));
    try {
      const user = await UserAdminService.create(request);
      await dispatch(getUsers());
      dispatch(createUserSuccessAction(user));
      await dispatch(toastSuccess(`${user.firstName} ${user.lastName} created`));
    } catch (error: any) {
      await dispatch(toastError(`Failed to create ${request.firstName} ${request.lastName}`));
      dispatch(deleteUserFailureAction(error));
    }
  };

export const setCurrentUser =
  (userSlim?: UserSlim): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(loadUserAction());
    try {
      const user = userSlim ? await UserAdminService.getById(userSlim.id) : undefined;
      dispatch(clientSuccessAction(user));
    } catch (error: any) {
      dispatch(clientFailureAction(error));
    }
  };

export const updateUser =
  (request: UpdateUserRequest): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    try {
      const user = request ? await UserAdminService.putById(request) : undefined;
      if (!user) {
        throw new Error(`Failed to update user with id ${request.id}`);
      }
      await dispatch(getUsers());
      await dispatch(userUpdateSuccessAction(user));
      await dispatch(toastSuccess(`${request.firstName} ${request.lastName} updated`));
    } catch (error: any) {
      await dispatch(toastError(`Failed to update ${request.firstName} ${request.lastName}`));
      dispatch(userUpdateFailureAction(error));
    }
  };
